import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, articleCategories } from '@/mocks/articles';

const articleCoverImages: Record<string, string> = {
  'article-01': 'https://readdy.ai/api/search-image?query=Elegant%20oriental%20aesthetic%20editorial%20photography%2C%20warm%20earth%20tones%2C%20minimal%20composition%20with%20natural%20textures%2C%20soft%20diffused%20lighting%2C%20calm%20poetic%20zen%20atmosphere%2C%20muted%20sage%20green%20and%20cream%20palette&width=600&height=400&seq=art-cover-01&orientation=landscape',
  'article-02': 'https://readdy.ai/api/search-image?query=Oriental%20botanical%20editorial%20photography%20of%20fresh%20herbs%20and%20flowers%20on%20rice%20paper%2C%20warm%20amber%20tones%2C%20minimal%20composition%2C%20soft%20natural%20lighting%2C%20traditional%20Chinese%20medicine%20aesthetic&width=600&height=400&seq=art-cover-02&orientation=landscape',
  'article-03': 'https://readdy.ai/api/search-image?query=Elegant%20tea%20ceremony%20editorial%20photography%2C%20steaming%20teacup%20on%20wooden%20table%2C%20warm%20golden%20lighting%2C%20minimal%20oriental%20aesthetic%2C%20serene%20atmosphere%2C%20soft%20cream%20and%20terracotta%20tones&width=600&height=400&seq=art-cover-03&orientation=landscape',
  'article-04': 'https://readdy.ai/api/search-image?query=Minimalist%20oriental%20living%20space%20editorial%20photography%2C%20ceramic%20vase%20with%20single%20branch%2C%20warm%20natural%20lighting%2C%20textured%20cream%20walls%2C%20zen%20simplicity%2C%20calm%20poetic%20atmosphere&width=600&height=400&seq=art-cover-04&orientation=landscape',
  'article-05': 'https://readdy.ai/api/search-image?query=Oriental%20fragrance%20editorial%20photography%2C%20essential%20oil%20bottles%20with%20dried%20lavender%2C%20warm%20golden%20lighting%2C%20minimal%20elegant%20composition%2C%20soft%20cream%20background%2C%20poetic%20atmosphere&width=600&height=400&seq=art-cover-05&orientation=landscape',
  'article-06': 'https://readdy.ai/api/search-image?query=Minimalist%20oriental%20interior%20photography%2C%20clean%20living%20room%20with%20wooden%20furniture%2C%20warm%20natural%20lighting%2C%20zen%20simplicity%2C%20soft%20cream%20and%20terracotta%20tones&width=600&height=400&seq=art-cover-06&orientation=landscape',
};

export default function Articles() {
  const [activeCat, setActiveCat] = useState<string>('all');

  const filtered = activeCat === 'all'
    ? articles
    : articles.filter((a) => a.category === activeCat);

  return (
    <>
      <SEOHead
        title="文章列表"
        description="品味东方生活美学的深度内容——品牌故事、诗经文化、生活美学、产品灵感。在呦呦的文字世界里，感受千年文化的当代回响。"
        canonicalUrl="https://yooyoozone.github.io/articles"
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '文章' },
            ]}
          />

          <div className="mb-10">
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
              文章
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              品味东方生活美学的深度内容
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-10">
            <button
              onClick={() => setActiveCat('all')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeCat === 'all'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </button>
            {articleCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCat(cat.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCat === cat.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Article Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 pb-16">
            {filtered.map((article) => (
              <Link
                key={article.id}
                to={`/articles/${article.slug}`}
                className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="h-48 md:h-52 overflow-hidden">
                  <img
                    src={articleCoverImages[article.coverImageSeq]}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-400 mb-2">
                    <span>{article.date}</span>
                    <span>·</span>
                    <span>{article.readTime}</span>
                    <span>·</span>
                    <span className="bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full text-[10px]">
                      {articleCategories.find((c) => c.id === article.category)?.name}
                    </span>
                  </div>
                  <h2 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                    {article.title}
                  </h2>
                  <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {article.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-[10px] text-secondary-600 bg-secondary-100 px-2 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}